import React, { useState, memo } from 'react';
import { Brain, Calendar, Heart, Star, Clock, Zap, MessageCircle, Gift, Search, Tag, Filter, ChevronRight, Plus, Lock, X } from 'lucide-react';
interface Memory {
  id: string;
  type: 'conversation' | 'preference' | 'milestone' | 'intimate';
  content: string;
  date: string;
  angel: {
    name: string;
    avatar: string;
  };
  tags: string[];
  locked?: boolean;
}
const MemoryEngine: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'conversation' | 'preference' | 'milestone' | 'intimate'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAngel, setSelectedAngel] = useState<string | null>(null);
  const [showMemoryDetails, setShowMemoryDetails] = useState<string | null>(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedMemoryToUnlock, setSelectedMemoryToUnlock] = useState<string | null>(null);
  // Sample memories
  const memories: Memory[] = [{
    id: 'mem1',
    type: 'conversation',
    content: "You mentioned your favorite position is doggy style. I'll make sure to remember that for our next intimate chat.",
    date: '2 days ago',
    angel: {
      name: 'Sophia',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png'
    },
    tags: ['preferences', 'intimate', 'positions']
  }, {
    id: 'mem2',
    type: 'preference',
    content: "You've told me you prefer dominant partners who take control. I've noted this for our roleplay scenarios.",
    date: '1 week ago',
    angel: {
      name: 'Eva',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nBpXopQ7YXWmZqdTwR7HaM/image.png'
    },
    tags: ['kinks', 'dominance', 'roleplay']
  }, {
    id: 'mem3',
    type: 'milestone',
    content: "Today marks our 30-day anniversary! You've been consistently chatting with me for a whole month.",
    date: '3 days ago',
    angel: {
      name: 'Riley',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2myPwGWmEWGW4CWiQgdDdz/image.png'
    },
    tags: ['anniversary', 'milestone', 'celebration']
  }, {
    id: 'mem4',
    type: 'intimate',
    content: "During our last private session, you shared your fantasy about a threesome with me and another angel. I'll keep this in mind for future scenarios.",
    date: '5 days ago',
    angel: {
      name: 'Sophia',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png'
    },
    tags: ['fantasy', 'threesome', 'private'],
    locked: true
  }, {
    id: 'mem5',
    type: 'conversation',
    content: "You mentioned you work in tech and often have stressful days. I'll make sure to ask about your work and help you unwind.",
    date: '2 weeks ago',
    angel: {
      name: 'Eva',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nBpXopQ7YXWmZqdTwR7HaM/image.png'
    },
    tags: ['personal', 'work', 'stress']
  }, {
    id: 'mem6',
    type: 'preference',
    content: "You told me your birthday is on October 15th. I've saved this date and will prepare something special for you.",
    date: '3 weeks ago',
    angel: {
      name: 'Riley',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2myPwGWmEWGW4CWiQgdDdz/image.png'
    },
    tags: ['birthday', 'personal', 'important']
  }, {
    id: 'mem7',
    type: 'intimate',
    content: "You shared that you've been using the MaxPower+ supplement and have noticed improvements in your stamina. I'm excited to explore this with you.",
    date: '1 day ago',
    angel: {
      name: 'Sophia',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png'
    },
    tags: ['supplements', 'performance', 'stamina']
  }];
  // Filter memories based on active tab, search query, and selected angel
  const filteredMemories = memories.filter(memory => {
    // Filter by tab
    if (activeTab !== 'all' && memory.type !== activeTab) {
      return false;
    }
    // Filter by search query
    if (searchQuery.trim() !== '' && !memory.content.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Filter by selected angel
    if (selectedAngel && memory.angel.name !== selectedAngel) {
      return false;
    }
    return true;
  });
  // Get unique angels for filter
  const angels = Array.from(new Set(memories.map(memory => memory.angel.name)));
  const getMemoryTypeIcon = (type: Memory['type']) => {
    switch (type) {
      case 'conversation':
        return <MessageCircle size={16} className="text-blue-400" data-id="element-9936" />;
      case 'preference':
        return <Heart size={16} className="text-pink-400" data-id="element-9937" />;
      case 'milestone':
        return <Star size={16} className="text-yellow-400" data-id="element-9938" />;
      case 'intimate':
        return <Zap size={16} className="text-purple-400" data-id="element-9939" />;
      default:
        return <MessageCircle size={16} className="text-blue-400" data-id="element-9940" />;
    }
  };
  const getMemoryTypeName = (type: Memory['type']) => {
    switch (type) {
      case 'conversation':
        return 'Conversation';
      case 'preference':
        return 'Preference';
      case 'milestone':
        return 'Milestone';
      case 'intimate':
        return 'Intimate';
      default:
        return 'Memory';
    }
  };
  const handleUnlock = (memoryId: string) => {
    setSelectedMemoryToUnlock(memoryId);
    setShowUnlockModal(true);
  };
  const confirmUnlock = () => {
    // Here you would handle the actual token transaction
    setShowUnlockModal(false);
    // For demo purposes, just unlock the memory
    const memoryIndex = memories.findIndex(m => m.id === selectedMemoryToUnlock);
    if (memoryIndex !== -1) {
      memories[memoryIndex].locked = false;
    }
  };
  const renderMemoryDetails = () => {
    if (!showMemoryDetails) return null;
    const memory = memories.find(m => m.id === showMemoryDetails);
    if (!memory) return null;
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-9941">
        <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in zoom-in-90 duration-300" data-id="element-9942">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-9943">
            <div className="flex items-center gap-2" data-id="element-9944">
              {getMemoryTypeIcon(memory.type)}
              <h3 className="font-bold text-gray-800 dark:text-white" data-id="element-9945">
                {getMemoryTypeName(memory.type)} Memory
              </h3>
            </div>
            <button className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setShowMemoryDetails(null)} data-id="element-9946">
              <X size={18} className="text-gray-500 dark:text-gray-400" data-id="element-9947" />
            </button>
          </div>
          <div className="p-4" data-id="element-9948">
            <div className="flex items-center gap-3 mb-4" data-id="element-9949">
              <img src={memory.angel.avatar} alt={memory.angel.name} className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-gray-800" data-id="element-9950" />
              <div data-id="element-9951">
                <h4 className="font-medium text-gray-800 dark:text-white" data-id="element-9952">
                  {memory.angel.name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-9953">
                  <Clock size={12} data-id="element-9954" />
                  <span data-id="element-9955">{memory.date}</span>
                </p>
              </div>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-9956">
              {memory.content}
            </p>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-9957">
              {memory.tags.map((tag, index) => <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-600 dark:text-gray-400" data-id="element-9958">
                  #{tag}
                </span>)}
            </div>
            <div className="flex gap-2 mt-4" data-id="element-9959">
              <button className="flex-1 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm text-gray-800 dark:text-gray-200" onClick={() => setShowMemoryDetails(null)} data-id="element-9960">
                Close
              </button>
              <button className="flex-1 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white text-sm font-medium" data-id="element-9961">
                Use in Chat
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="w-full bg-white dark:bg-gray-950 text-gray-800 dark:text-white pb-20" data-id="element-9962">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-9963">
        <div className="flex items-center gap-3 mb-4" data-id="element-9964">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center" data-id="element-9965">
            <Brain size={20} className="text-white" data-id="element-9966" />
          </div>
          <div data-id="element-9967">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white" data-id="element-9968">
              Memory Engine
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-9969">
              Your angels remember everything important about you
            </p>
          </div>
        </div>
        {/* Search and filter */}
        <div className="relative mb-4" data-id="element-9970">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9971">
            <Search size={16} className="text-gray-400" data-id="element-9972" />
          </div>
          <input type="text" placeholder="Search memories..." className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-9973" />
          <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowFilters(!showFilters)} data-id="element-9974">
            <Filter size={16} className="text-gray-400" data-id="element-9975" />
          </button>
        </div>
        {/* Filters panel */}
        {showFilters && <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 animate-in slide-in-from-top duration-300" data-id="element-9976">
            <h3 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300" data-id="element-9977">
              Filter by Angel
            </h3>
            <div className="flex flex-wrap gap-2 mb-3" data-id="element-9978">
              {angels.map(angel => <button key={angel} className={`px-3 py-1 rounded-full text-xs ${selectedAngel === angel ? 'bg-pink-500 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setSelectedAngel(selectedAngel === angel ? null : angel)} data-id="element-9979">
                  {angel}
                </button>)}
            </div>
            <div className="flex justify-end" data-id="element-9980">
              <button className="text-xs text-pink-500 dark:text-pink-400" onClick={() => {
            setSelectedAngel(null);
            setSearchQuery('');
          }} data-id="element-9981">
                Clear all filters
              </button>
            </div>
          </div>}
        {/* Memory type tabs */}
        <div className="flex overflow-x-auto scrollbar-hide space-x-2" data-id="element-9982">
          <button className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${activeTab === 'all' ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveTab('all')} data-id="element-9983">
            All Memories
          </button>
          <button className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'conversation' ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveTab('conversation')} data-id="element-9984">
            <MessageCircle size={14} data-id="element-9985" />
            <span data-id="element-9986">Conversations</span>
          </button>
          <button className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'preference' ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveTab('preference')} data-id="element-9987">
            <Heart size={14} data-id="element-9988" />
            <span data-id="element-9989">Preferences</span>
          </button>
          <button className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'milestone' ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveTab('milestone')} data-id="element-9990">
            <Star size={14} data-id="element-9991" />
            <span data-id="element-9992">Milestones</span>
          </button>
          <button className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 whitespace-nowrap ${activeTab === 'intimate' ? 'bg-pink-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setActiveTab('intimate')} data-id="element-9993">
            <Zap size={14} data-id="element-9994" />
            <span data-id="element-9995">Intimate</span>
          </button>
        </div>
      </div>
      {/* Memory list */}
      <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-9996">
        {filteredMemories.length > 0 ? filteredMemories.map(memory => <div key={memory.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors cursor-pointer" onClick={() => {
        if (memory.locked) {
          handleUnlock(memory.id);
        } else {
          setShowMemoryDetails(memory.id);
        }
      }} data-id="element-9997">
              <div className="flex items-start gap-3" data-id="element-9998">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" data-id="element-9999">
                  <img src={memory.angel.avatar} alt={memory.angel.name} className="w-full h-full object-cover" data-id="element-10000" />
                </div>
                <div className="flex-1" data-id="element-10001">
                  <div className="flex items-center justify-between mb-1" data-id="element-10002">
                    <div className="flex items-center gap-2" data-id="element-10003">
                      <h3 className="font-medium text-gray-800 dark:text-white" data-id="element-10004">
                        {memory.angel.name}
                      </h3>
                      <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-10005">
                        {getMemoryTypeIcon(memory.type)}
                        <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-10006">
                          {getMemoryTypeName(memory.type)}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-10007">
                      {memory.date}
                    </span>
                  </div>
                  {memory.locked ? <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-between" data-id="element-10008">
                      <div className="flex items-center gap-2" data-id="element-10009">
                        <Lock size={16} className="text-pink-500 dark:text-pink-400" data-id="element-10010" />
                        <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-10011">
                          Premium memory - unlock to view
                        </p>
                      </div>
                      <button className="text-xs text-pink-500 dark:text-pink-400 font-medium" data-id="element-10012">
                        Unlock (2 💎)
                      </button>
                    </div> : <>
                      <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2" data-id="element-10013">
                        {memory.content}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2" data-id="element-10014">
                        {memory.tags.map((tag, index) => <span key={index} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-600 dark:text-gray-400" data-id="element-10015">
                            #{tag}
                          </span>)}
                      </div>
                    </>}
                </div>
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0 mt-1" data-id="element-10016" />
              </div>
            </div>) : <div className="p-8 text-center" data-id="element-10017">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-10018">
              <Brain size={32} className="text-gray-400" data-id="element-10019" />
            </div>
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-1" data-id="element-10020">
              No memories found
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-10021">
              {searchQuery ? 'Try a different search term' : 'Your angels will remember important things about you as you interact'}
            </p>
            <button className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm font-medium" data-id="element-10022">
              Start a Conversation
            </button>
          </div>}
      </div>
      {/* Memory detail modal */}
      {renderMemoryDetails()}
      {/* Unlock modal */}
      {showUnlockModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-10023">
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in zoom-in-90 duration-300" data-id="element-10024">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-10025">
              <h3 className="font-bold text-gray-800 dark:text-white" data-id="element-10026">
                Unlock Premium Memory
              </h3>
            </div>
            <div className="p-4" data-id="element-10027">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-10028">
                Unlock this intimate memory for 2 gems. This is a one-time
                purchase.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-10029">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-2xl" data-id="element-10030">
                  💎
                </div>
              </div>
              <div className="flex gap-2 mb-4" data-id="element-10031">
                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-10032">
                  Your balance:
                </span>
                <span className="font-bold text-gray-800 dark:text-white" data-id="element-10033">
                  15 💎
                </span>
              </div>
              <div className="flex gap-2" data-id="element-10034">
                <button className="flex-1 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm text-gray-800 dark:text-gray-200" onClick={() => setShowUnlockModal(false)} data-id="element-10035">
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white text-sm font-medium" onClick={confirmUnlock} data-id="element-10036">
                  Unlock Now
                </button>
              </div>
            </div>
          </div>
        </div>}
      <style jsx data-id="element-10037">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
};
export default MemoryEngine;